import { getCurrencySymbol } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ClearFormDataActionWithId,
  UpdateFormDataActionWithId,
} from '@app/finxone-web-frontend/app/lib/actions/form-submission.action';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { AccountState, AccountStateModel } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { currencyToCircleFlag } from '@app/finxone-web-frontend/app/lib/utils/template/handlebar-helpers/currency-to-flag.utils';
import { FormActionTypeEnum } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  AccountInfoType,
  BaseCssProperties,
  ExchangeFixedType,
  MultiCurrencyAccountInfoType,
  ProviderClass,
  UiZoneWidgetConfig,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
  isMultiCurrencyAccountInfoType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, debounceTime, map, takeUntil } from 'rxjs';
import { ConfigService } from '../../../../lib/services/config-service/config-service.service';
import { BaseComponent } from '../base/base.component';

import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import {
  buildHandleBarModel,
  filterIfHandlebarNotRendered,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { resolveAccountIdentifierFields } from '@app/finxone-web-frontend/app/lib/utils/account-identifier.utils';
import { addDashesToSortCode } from '@app/finxone-web-frontend/app/lib/utils/template/string.utils';
import Handlebars from 'handlebars';
import { PageConfigurationProvider } from '../../../zones/page-configuration.provider';
import { PARENT_FORM_ROW_GAP } from '@app/finxone-web-frontend/app/lib/utils/di-tokens';

const sellCurrencyVisualTemplate = `<div class="w-100 is-space-between">
    {{ currency }}
    <div> Available: {{ currencyPipe availableBalance currencyCode=currency }} </div>
</div>`;

export type AccountDetails = AccountInfoType & {
  key: string;
  title: string;
  currencyFlag: string | undefined;
};

const SELL_CONTROL = 'sellAccountId';
const BUY_CONTROL = 'buyAccountId';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-exchange-currency-widget',
  templateUrl: './exchange-currency-widget.component.html',
  styleUrls: ['./exchange-currency-widget.component.scss'],
})
export class ExchangeCurrencyWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  public zone = 'payment';
  public widgetConfig: UiZoneWidgetConfig;
  exchangeCurrencyForm: FormGroup;
  isError = false;
  urlParams: string[] = [];
  isShowErrorMsg = false;
  colorTextAlignStyle: baseCssConfigStyle;
  colorStyle: baseCssConfigStyle;
  borderStyle: baseCssConfigStyle;
  public buyAccountList: AccountDetails[] = [];
  public sellAccountList: AccountDetails[] = [];
  currency = 'GBP';

  @Select(AccountState.getAccounts)
  accountStateData!: Observable<AccountStateModel>;
  private accountsData: AccountInfoType[];
  public currentStyle: baseCssConfigStyle;
  public showFlags = false;

  public quoteButtonStyle: baseCssConfigStyle = {};
  public quoteButtonContent = 'Quote';

  public defaultBuyAccount: AccountDetails | undefined;
  public defaultSellAccount: AccountDetails | undefined;
  public readonly currencyDropdownPlaceholder = 'Select currency';

  public sellCurrencyOptionTemplateFunction: HandlebarsTemplateDelegate<any>;
  public buyCurrencyOptionTemplateFunction: HandlebarsTemplateDelegate<any>;
  public buySelectedItemTemplateFunction: HandlebarsTemplateDelegate<any>;
  public sellSelectedItemTemplateFunction: HandlebarsTemplateDelegate<any>;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public renderedContent = '';
  public renderedSelectedSellCurrencyContent: string;
  public renderedSelectedBuyCurrencyContent: string;

  public parentFormWidgetRowGap: string | undefined | null = inject(PARENT_FORM_ROW_GAP, { optional: true });

  constructor(
    private configService: ConfigService,
    public dataIn: ProviderClass<any>,
    private formSubmissionService: FormSubmissionService,
    private _router: Router,
    private alertHandlerService: AlertHandlerService,
    private store: Store,
    public pageConfig: PageConfigurationProvider,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private accountService: AccountService,
  ) {
    super();
  }

  get sellSelectedItemTemplate() {
    return this.dataIn?.attri?.widgetProperties?.sellCurrencyVisualTemplate ?? sellCurrencyVisualTemplate;
  }

  get sellCurrencyOptionTemplate() {
    return this.dataIn?.attri?.widgetProperties?.sellCurrencyOptionTemplate ?? `{{ currency }}`;
  }
  get buyCurrencyOptionTemplate() {
    return this.dataIn?.attri?.widgetProperties?.buyCurrencyOptionTemplate ?? `{{ currency }}`;
  }

  get buySelectedItemTemplate() {
    return this.dataIn?.attri?.widgetProperties?.buyCurrencyVisualTemplate ?? `{{ currency }}`;
  }

  ngOnInit(): void {
    if (this.dataIn != undefined) {
      this.widgetConfig = this.dataIn.widgetConfig;
      this.attri = this.dataIn.attri;
      this.sellCurrencyOptionTemplateFunction = Handlebars.compile(this.sellCurrencyOptionTemplate);
      this.buyCurrencyOptionTemplateFunction = Handlebars.compile(this.buyCurrencyOptionTemplate);
      this.sellSelectedItemTemplateFunction = Handlebars.compile(this.sellSelectedItemTemplate);
      this.buySelectedItemTemplateFunction = Handlebars.compile(this.buySelectedItemTemplate);
    }
    this.accountStateData
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$),
        map((accountData: AccountStateModel) => {
          this.accountsData = accountData.accounts;
          this.setAccountListData(accountData);
        }),
      )
      .subscribe();
    this.buildForm();

    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BORDER,
      BaseCssProperties.COLOR,
      BaseCssProperties.BACKGROUND_COLOR,
    ]);

    this.colorTextAlignStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.COLOR,
      BaseCssProperties.TEXTALIGN,
    ]);

    this.colorStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.COLOR]);

    this.borderStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.BORDER]);

    this.onLoadWidgetContent();
    this.quoteButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(this.dataIn, 'quoteButton'),
      buttonNestedWidgetConfigStyles,
    );

    this.updateStyleProperties();
    this.getGlobalStyling();
  }

  private onLoadWidgetContent(): void {
    this.quoteButtonContent =
      this.dataIn.widgetConfig.nestedWidgets?.['quoteButton'].widgetProperties.textContent ?? 'Quote';
    const template = Handlebars.compile(this.quoteButtonContent);
    buildHandleBarModel(
      this.store,
      this.quoteButtonContent,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.accountService,
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.renderedContent = filterIfHandlebarNotRendered(template(model));
      });
  }

  private renderDropdownSelectedTemplate() {
    this.renderedSelectedSellCurrencyContent = this.renderTemplate(
      this.sellSelectedItemTemplate,
      this.defaultSellAccount,
    );
    this.renderedSelectedBuyCurrencyContent = this.renderTemplate(
      this.buySelectedItemTemplate,
      this.defaultBuyAccount,
    );
  }

  private renderTemplate(template: string, account: AccountDetails | undefined): string {
    if (!account) {
      // Nothing selected in this dropdown — the template renders the placeholder instead.
      return '';
    }
    const templateFunction = Handlebars.compile(template);
    const model = {
      ...this.buildAccountModel(account),
    };
    return templateFunction(model);
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['quoteButton'].widgetProperties?.['globalStylingClass'] &&
      this.dataIn.widgetConfig.nestedWidgets?.['quoteButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['quoteButton'].widgetProperties?.['globalStylingClass'] ??
        '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['quoteButton'].widgetProperties?.['globalStyling'] ?? false;
    }
  }

  private getCurrencyByAccountId = (accountId: string | undefined) => {
    const accountData = this.store.selectSnapshot(AccountState.getAccounts);
    const selectedAccount = accountData.accounts.find((account) => account.accountId === accountId);
    return selectedAccount && isMultiCurrencyAccountInfoType(selectedAccount)
      ? (accountData.currentMultiCurrencyCode ?? 'GBP')
      : (selectedAccount?.currency ?? 'GBP');
  };

  private buildMultiCurrencyEntries(account: MultiCurrencyAccountInfoType): AccountDetails[] {
    return account.currency.map((c) => {
      const availBal = account.availableBalance.find((b) => b.currency === c.currency)?.amount ?? '0';
      return {
        key: c.currency,
        title: c.currency,
        currencyFlag: this.widgetConfig.attributes.widgetProperties.inputFieldSettings?.showFlags
          ? currencyToCircleFlag(c.currency, '24px')
          : undefined,
        ...this.buildAccountModel(account, c.currency),
        currency: c.currency,
        availableBalance: availBal,
      } as AccountDetails;
    });
  }

  private setAccountListData(accountData: AccountStateModel) {
    const currentAccountId = accountData.currentAccountId;
    const currentAccount = accountData.accounts.find((a) => a.accountId === currentAccountId);
    // clear form state to start a fresh exchange
    this.store.dispatch(new ClearFormDataActionWithId(FormActionTypeEnum.CURRENCY_EXCHANGE));

    // Both dropdowns offer the full list. A duplicate pick is resolved by clearing the opposite
    // dropdown rather than by hiding options, so every currency stays reachable from either one.
    const options =
      currentAccount && isMultiCurrencyAccountInfoType(currentAccount)
        ? // Expand the single account object into one entry per currency
          this.buildMultiCurrencyEntries(currentAccount)
        : this.buildSingleCurrencyEntries(accountData);
    this.sellAccountList = options;
    this.buyAccountList = options;

    // Default to a distinct pair: sell the first option that isn't the account in context,
    // buy the account in context (multi-currency keys are currency codes, so it falls through
    // to the first option that isn't the sell selection).
    this.defaultSellAccount =
      this.sellAccountList.find((account) => account.key !== currentAccountId) ?? this.sellAccountList[0];
    this.defaultBuyAccount =
      this.buyAccountList.find((account) => account.key === currentAccountId) ??
      this.buyAccountList.find((account) => account.key !== this.defaultSellAccount?.key);
    this.exchangeCurrencyForm.get(BUY_CONTROL)?.setValue(this.defaultBuyAccount?.key ?? '');
    this.exchangeCurrencyForm.get(SELL_CONTROL)?.setValue(this.defaultSellAccount?.key ?? '');

    const contextKey =
      this.attri.widgetProperties['context'] === 'Buy'
        ? this.defaultBuyAccount?.key
        : this.defaultSellAccount?.key;
    this.currency =
      currentAccount && isMultiCurrencyAccountInfoType(currentAccount)
        ? (contextKey ?? 'GBP')
        : this.getCurrencyByAccountId(contextKey);
    this.renderDropdownSelectedTemplate();
  }

  private buildSingleCurrencyEntries(accountData: AccountStateModel): AccountDetails[] {
    return accountData.accounts.map((item) => {
      const currency = isMultiCurrencyAccountInfoType(item) ? '' : item.currency;
      return {
        key: item.accountId,
        title: currency,
        currencyFlag:
          this.widgetConfig.attributes.widgetProperties.inputFieldSettings?.showFlags && currency
            ? currencyToCircleFlag(currency, '24px')
            : undefined,
        ...this.buildAccountModel(item),
      } as AccountDetails;
    });
  }

  public createSellCurrencyItemTemplate(item: any): string {
    if (this.sellCurrencyOptionTemplateFunction) {
      return this.sellCurrencyOptionTemplateFunction(item);
    }
    return ''; // fallback in case template is missing
  }

  public createBuyCurrencyItemTemplate(item: any): string {
    if (this.buyCurrencyOptionTemplateFunction) {
      return this.buyCurrencyOptionTemplateFunction(item);
    }
    return ''; // fallback in case template is missing
  }

  // onSelectionChange already clears the duplicate, so this only catches a matching pair that never
  // went through the dropdowns (restored form state, or a default pair with nothing distinct to pick).
  private distinctCurrencyPairValidator(group: AbstractControl): ValidationErrors | null {
    const sellKey = group.get(SELL_CONTROL)?.value;
    const buyKey = group.get(BUY_CONTROL)?.value;
    return sellKey && buyKey && sellKey === buyKey ? { sameCurrency: true } : null;
  }

  private buildForm() {
    const formGroup = new FormGroup({}, this.distinctCurrencyPairValidator.bind(this));
    for (const field of this.widgetConfig.attributes['field_attributes']) {
      // A cleared currency control must block the quote until a distinct currency is picked.
      const validators =
        field.name === SELL_CONTROL || field.name === BUY_CONTROL ? [Validators.required] : [];
      formGroup.addControl(field.name, new FormControl('', validators));
    }

    if (this.widgetConfig.attributes?.widgetProperties?.inputFieldSettings?.showFlags) {
      this.showFlags = this.widgetConfig.attributes?.widgetProperties?.inputFieldSettings?.showFlags;
    }

    this.exchangeCurrencyForm = formGroup;
  }
  private updateStyleProperties() {
    this.contentStyle = {
      ...this.contentStyle,
      display: 'block',
    };
  }

  getAccountById(accountId: string): AccountInfoType | undefined {
    return this.accountsData.find((account) => account.accountId === accountId);
  }

  checkAmountValidation() {
    let selectedAccount;

    const valueExchange = this.exchangeCurrencyForm.value?.['amount'].replace(/,/g, '');
    const amount = Number(valueExchange);

    if (Number(amount) < 1) {
      this.alertHandlerService.showAlertFn('error', 'Amount must be greater than or equal to 1');
      return false;
    }

    if (this.attri.widgetProperties.context === 'Sell') {
      const sellKey = this.exchangeCurrencyForm.value.sellAccountId;
      // First check if sellKey matches an accountId (regular account). If not, treat it as a currency code for a multi-currency account
      selectedAccount = this.getAccountById(sellKey);

      if (!selectedAccount) {
        // Multi-currency: sellKey is a currency code — look up balance from the multi-currency account
        const accountData = this.store.selectSnapshot(AccountState.getAccounts);
        const multiCurrencyAccount = accountData.accounts.find(
          (a) => a.accountId === accountData.currentAccountId,
        );
        if (multiCurrencyAccount && isMultiCurrencyAccountInfoType(multiCurrencyAccount)) {
          const balEntry = multiCurrencyAccount.availableBalance.find((b) => b.currency === sellKey);
          const multiCurrencyAccountBalance = Number(balEntry?.amount ?? 0);
          if (multiCurrencyAccountBalance < amount) {
            this.alertHandlerService.showAlertFn(
              'error',
              'Enter a lower amount to continue.',
              'Insufficient balance.',
            );
            return false;
          }
        }
      } else {
        const accountBalance = isMultiCurrencyAccountInfoType(selectedAccount)
          ? 0
          : Number(selectedAccount.balance);
        if (isNaN(amount) || isNaN(accountBalance)) {
          console.error('Account balance or payment amount NaN');
        }
        if (accountBalance < amount) {
          this.alertHandlerService.showAlertFn(
            'error',
            'Enter a lower amount to continue.',
            'Insufficient balance.',
          );
          return false;
        }
      }
    }
    // if buy side don't check amount as can't know before the quote whether we can cover the trade
    return true;
  }

  public onSubmit() {
    if (!this.checkAmountValidation()) {
      return;
    }
    if (this.exchangeCurrencyForm.valid) {
      if (
        this.exchangeCurrencyForm.value?.['amount'] !== undefined &&
        this.exchangeCurrencyForm.value?.['amount'] === ''
      ) {
        this.alertHandlerService.showAlertFn('error', 'Amount field should not be empty.');
        return;
      }
      const formObject = this.exchangeCurrencyForm.value;
      const fixedType: ExchangeFixedType =
        this.attri.widgetProperties['context'] === 'Buy' ? ExchangeFixedType.BUY : ExchangeFixedType.SELL;
      const accountData = this.store.selectSnapshot(AccountState.getAccounts);
      const currentAccount = accountData.accounts.find((a) => a.accountId === accountData.currentAccountId);
      const isMultiCurrency = (currentAccount && isMultiCurrencyAccountInfoType(currentAccount)) as boolean;
      this.formSubmissionService.submitIndividualForm(
        this.buildExchangePayload(formObject, fixedType, isMultiCurrency, accountData.currentAccountId),
        '',
        FormActionTypeEnum.CURRENCY_EXCHANGE,
      );
      this.isError = false;
      this._router.navigateByUrl('/zones/payments/exchange-quote');
    } else {
      this.exchangeCurrencyForm.markAllAsTouched();
      this.isError = true;
    }
  }

  /**
   * Handles a pick on either dropdown.
   *
   * Sell and Buy can never hold the same currency, so choosing the currency the other dropdown
   * already holds resets that dropdown to its placeholder instead of blocking the pick. The quote
   * stays disabled (the control is `required`) until a distinct currency is chosen for it.
   */
  public onSelectionChange(context: 'Buy' | 'Sell'): void {
    const clearedControl = context === 'Buy' ? SELL_CONTROL : BUY_CONTROL;
    const selectedControl = context === 'Buy' ? BUY_CONTROL : SELL_CONTROL;
    const selectedKey = this.exchangeCurrencyForm.get(selectedControl)?.value;

    if (selectedKey && selectedKey === this.exchangeCurrencyForm.get(clearedControl)?.value) {
      this.exchangeCurrencyForm.get(clearedControl)?.setValue('', { emitEvent: false });
    }

    this.valueChanged();
  }

  public valueChanged() {
    this.defaultSellAccount = this.findAccountByKey(
      this.sellAccountList,
      this.exchangeCurrencyForm.get(SELL_CONTROL)?.value,
    );
    this.defaultBuyAccount = this.findAccountByKey(
      this.buyAccountList,
      this.exchangeCurrencyForm.get(BUY_CONTROL)?.value,
    );
    this.renderDropdownSelectedTemplate();

    const accountData = this.store.selectSnapshot(AccountState.getAccounts);
    const currentAccount = accountData.accounts.find((a) => a.accountId === accountData.currentAccountId);

    const contextKey =
      this.attri.widgetProperties['context'] === 'Buy'
        ? this.defaultBuyAccount?.key
        : this.defaultSellAccount?.key;
    this.currency =
      currentAccount && isMultiCurrencyAccountInfoType(currentAccount)
        ? (contextKey ?? 'GBP')
        : this.getCurrencyByAccountId(contextKey);

    // Only publish a complete pair — a dropdown cleared by a duplicate pick has nothing to exchange yet.
    if (!this.defaultSellAccount || !this.defaultBuyAccount) {
      return;
    }
    const exchangeUpdate =
      currentAccount && isMultiCurrencyAccountInfoType(currentAccount)
        ? {
            buyAccountId: accountData.currentAccountId,
            sellAccountId: accountData.currentAccountId,
            buyCurrency: this.defaultBuyAccount.key,
            sellCurrency: this.defaultSellAccount.key,
          }
        : {
            buyAccountId: this.defaultBuyAccount.key,
            sellAccountId: this.defaultSellAccount.key,
          };
    this.store.dispatch(new UpdateFormDataActionWithId(exchangeUpdate, FormActionTypeEnum.CURRENCY_EXCHANGE));
  }

  private findAccountByKey(accounts: AccountDetails[], key: string | undefined) {
    return key ? accounts?.find((account) => account?.key === key) : undefined;
  }

  public get currencySymbol() {
    return getCurrencySymbol(this.currency, 'narrow') ?? '';
  }

  private buildExchangePayload(
    formObject: { buyAccountId: string; sellAccountId: string; amount: string },
    fixedType: ExchangeFixedType,
    isMultiCurrency: boolean,
    currentAccountId: string,
  ) {
    // For multi-currency accounts the dropdowns are currency pickers, not account pickers.
    // buildMultiCurrencyEntries() sets each entry's key to the currency code (e.g. "GBP"),
    // so formObject.buyAccountId / sellAccountId actually hold currency codes here.
    // The single underlying account ID is passed separately as buyAccountId / sellAccountId.
    if (isMultiCurrency) {
      return {
        buyAccountId: currentAccountId,
        sellAccountId: currentAccountId,
        buyCurrency: formObject.buyAccountId,
        sellCurrency: formObject.sellAccountId,
        amount: this.stripCommas(formObject.amount),
        fixedType,
      };
    }
    return {
      sellAccountId: formObject.sellAccountId,
      buyAccountId: formObject.buyAccountId,
      amount: this.stripCommas(formObject.amount),
      fixedType,
    };
  }

  private stripCommas(numberString: string) {
    return numberString.replace(/,/g, '');
  }

  private buildAccountModel(account: AccountInfoType, currency?: string) {
    // For multi-currency accounts each dropdown entry is a single currency, so the
    // identifier fields are resolved against that currency; otherwise resolve across
    // all identifiers (single-currency accounts have only one).
    const identifierFields = resolveAccountIdentifierFields(account, currency);
    return {
      ...account,
      sortCode: addDashesToSortCode(identifierFields.sortCode),
      accountNumber: identifierFields.accountNumber,
      iban: identifierFields.iban,
      bic: identifierFields.bic,
      achAccountNumber: identifierFields.achAccountNumber,
      achNumber: identifierFields.achNumber,
      abaAccountNumber: identifierFields.abaAccountNumber,
      abaNumber: identifierFields.abaNumber,
      accountProviderExtraInfo: identifierFields.providerExtraInfo,
      ledgerNumber: identifierFields.ledgerNumber,
      bsbCode: identifierFields.bsbCode,
      ifsc: identifierFields.ifsc,
      clabe: identifierFields.clabe,
      bankCode: identifierFields.bankCode,
      branchCode: identifierFields.branchCode,
      rtnCanada: identifierFields.rtnCanada,
    };
  }
}
